import { useRef, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { parseCheckoutParams } from './useCheckoutParams';

export const getSessionId = () => {
  const key = 'form_tracking_session';
  let id = sessionStorage.getItem(key);
  if (!id) {
    id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    sessionStorage.setItem(key, id);
  }
  return id;
};

export const getPersistentId = () => {
  const key = 'form_tracking_user';
  let id = localStorage.getItem(key);
  if (!id) {
    id = `u-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    localStorage.setItem(key, id);
  }
  return id;
};

// Entidad (UAI, CEE, ALB…) y tipo de programa (g/p/e/m) del link actual. Van en todo lo que se
// trackea: sin ellos, los eventos de colegios quedaban mezclados con los de la universidad y no
// habia forma de separarlos (el codigo de carrera puede repetirse entre entidades). T-2026-107.
const getEntidadYTipo = (search) => {
  const { e, t } = parseCheckoutParams(search);
  return { entidad: e, tipo: t };
};

// Corta TODA notificación de progreso hacia afuera (tracking + identity + lead de Zoho, que es el
// que hace que un asesor reciba "se quiere inscribir"). Para probar contra prod desde local:
// REACT_APP_DISABLE_TRACKING=true en .env.development.
// Es opt-out a propósito: si la variable no está, el tracking anda. Y CRA la resuelve en tiempo de
// BUILD, así que no es un switch en caliente del sitio publicado (ver nota en el README del cambio).
export const trackingDeshabilitado = () => process.env.REACT_APP_DISABLE_TRACKING === 'true';

export const identifyUser = (email, programData = {}) => {
  if (trackingDeshabilitado()) return Promise.resolve();
  sessionStorage.setItem('form_tracking_email', email);
  return fetch(`${process.env.REACT_APP_API_BASE_URL}/tracking/identity`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      appCode:      process.env.REACT_APP_TRACKING_CODE || null,
      sessionId:    getSessionId(),
      persistentId: getPersistentId(),
      email,
      ...getEntidadYTipo(window.location.search),
      ...programData,
    }),
  }).catch(() => {});
};

// abandono: solo el orquestador (CheckoutPage) registra el evento de abandono. Los formularios de cada
// paso usan el mismo hook y antes cada instancia montada registraba su propio listener: un cierre
// generaba dos form_abandoned (uno con el paso base 0 de la página y otro con el base 1 del form).
const useFormTracking = (stepNumber = 0, { abandono = false } = {}) => {
  const sessionId    = useRef(getSessionId());
  const persistentId = useRef(getPersistentId());
  const stepStartTime = useRef(Date.now());
  const { search } = useLocation();
  const { entidad, tipo } = getEntidadYTipo(search);

  const send = (payload) => {
    if (trackingDeshabilitado()) return;
    fetch(`${process.env.REACT_APP_API_BASE_URL}/tracking/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }).catch(() => {});
  };

  const track = useCallback(
    (eventName, { fieldName = null } = {}) => {
      const timeSpent = Math.round((Date.now() - stepStartTime.current) / 1000);
      send({
        appCode:      process.env.REACT_APP_TRACKING_CODE || null,
        sessionId:    sessionId.current,
        persistentId: persistentId.current,
        eventName,
        fieldName,
        stepNumber,
        timeSpent,
        entidad,
        tipo,
      });
    },
    [stepNumber, entidad, tipo]
  );

  const resetStepTimer = useCallback(() => {
    stepStartTime.current = Date.now();
  }, []);

  // Abandono: la persona cierra la pestaña, navega a otro sitio o refresca (el refresco también cuenta:
  // se distingue por el form_started siguiente con la misma sessionId).
  // Antes iba por navigator.sendBeacon con un Blob application/json en beforeunload y casi nunca llegaba
  // (9 eventos en 3 meses): sendBeacon manda credenciales y NBAPI responde el preflight con
  // Access-Control-Allow-Origin "*" sin Allow-Credentials, así que Chrome hacía el OPTIONS y descartaba
  // el POST. fetch con keepalive sobrevive al cierre de la página y pasa el CORS de NBAPI.
  // pagehide en vez de beforeunload: en celulares beforeunload muchas veces no se dispara.
  useEffect(() => {
    if (!abandono || trackingDeshabilitado()) return;
    const handleAbandonment = () => {
      const payload = JSON.stringify({
        appCode:      process.env.REACT_APP_TRACKING_CODE || null,
        sessionId:    sessionId.current,
        persistentId: persistentId.current,
        eventName:    'form_abandoned',
        fieldName:    null,
        stepNumber,
        timeSpent:    Math.round((Date.now() - stepStartTime.current) / 1000),
        entidad,
        tipo,
      });
      try {
        fetch(`${process.env.REACT_APP_API_BASE_URL}/tracking/event`, {
          method: 'POST',
          keepalive: true,
          headers: { 'Content-Type': 'application/json' },
          body: payload,
        }).catch(() => {});
      } catch (e) { /* nunca interrumpir el cierre */ }
    };

    window.addEventListener('pagehide', handleAbandonment);
    return () => window.removeEventListener('pagehide', handleAbandonment);
  }, [abandono, stepNumber, entidad, tipo]);

  return { track, resetStepTimer };
};

export default useFormTracking;
